import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConnectorDownload, ConnectorDownloads } from "../../api/connectorReleasesApi";
import { readConnectorServerAddress } from "../../api/connectorsApi";
import { useSession } from "../../session/sessionContext";
import { useLoad, type LoadState } from "../../shared/loading/useLoad";
import LoadStatus from "../../ui/feedback/LoadStatus";
import AppButton from "../../ui/buttons/AppButton";
import ButtonLink from "../../ui/buttons/ButtonLink";
import CopyButton from "../../ui/content/CopyButton";
import CommandLine from "./CommandLine";
import { browserSystem, connectorPairingLink, installCommand, offerFor, otherSystemsInstallers } from "./connectorLinks";

const SYSTEM_NAMES: Readonly<Record<string, string>> = { windows: "Windows", darwin: "macOS", linux: "Linux" };

function systemName(os: string, arch: string): string {
  return `${SYSTEM_NAMES[os] ?? os}${os === "darwin" ? (arch === "aarch64" ? " (Apple Silicon)" : " (Intel)") : ""}`;
}

/** How long "Open DMW Connector" waits for the app to take the focus before it offers the other ways. */
const APP_LINK_WAIT_MILLISECONDS = 2_000;

/**
 * Getting the connector without a console (F47, O-5) from this server's release channel
 * (`threads.connector_hub.release_folder`, its links on the server's public address): this
 * browser's system's installer, its SHA-256 behind Details (trust on first use, CI-9; P-I) and
 * the one warning an unsigned build meets there; or, on Linux and macOS, the one line that
 * installs it (RE-7). Without a release, it says so in one line with the next step, the
 * administrator's (D-03, TR-A1-01); a list that couldn't be read says that, never an empty step.
 */
export default function GetTheConnector({ downloads, reload }: { downloads: LoadState<ConnectorDownloads>; reload: () => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  if (downloads.status !== "loaded") return <LoadStatus state={downloads} loadingLabel={t("common.loading")} onRetry={reload} compact />;
  const { version, installScript } = downloads.value;
  const offered = downloads.value.downloads;
  if (version === null || offered.length === 0) {
    const isAdministrator = session.status === "signedIn" && session.session.account.accountRole === "administrator";
    return (
      <p className="app-text text-sm">
        {t("connectors.download.none")} {t(isAdministrator ? "connectors.download.noneAdministrator" : "connectors.download.noneMember")}
      </p>
    );
  }
  const system = browserSystem(navigator.userAgent);
  const offer = offerFor(downloads.value, system);
  const others = otherSystemsInstallers(offered, system, offer);
  const commandElsewhere = offer.kind !== "command" && installScript !== null && offered.some((download) => download.kind === "package");
  return (
    <div className="space-y-3">
      {offer.kind === "command" ? <InstallCommand command={offer.command} />
        : offer.kind === "installer" ? <Installer download={offer.download} version={version} />
        : <p className="app-text text-sm">{t("connectors.download.noneForSystem")}</p>}
      {others.length === 0 ? null : (
        <p className="app-text-muted text-xs">
          {t("connectors.download.otherSystems")}{" "}
          {others.map((download, index) => (
            <span key={download.file}>
              {index > 0 ? ", " : ""}
              <a className="app-link" href={download.url} download={download.file}>{systemName(download.os, download.arch)}</a>
            </span>
          ))}
        </p>
      )}
      {commandElsewhere ? (
        <div className="app-text-muted space-y-1 text-xs">
          <p>{t("connectors.download.commandElsewhere")}</p>
          <CommandWithCopy command={installCommand(installScript)} />
        </div>
      ) : null}
    </div>
  );
}

function Installer({ download, version }: { download: ConnectorDownload; version: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <ButtonLink href={download.url} download={download.file} variant="primary">
        {t("connectors.download.button", { system: systemName(download.os, download.arch), version, size: Math.max(1, Math.round(download.size / 1e6)) })}
      </ButtonLink>
      <p className="app-help-text text-xs">{t(download.os === "darwin" ? "connectors.download.macNote" : download.os === "windows" ? "connectors.download.windowsNote" : "connectors.download.linuxNote")}</p>
      <details className="app-text-muted text-xs">
        <summary className="cursor-pointer">{t("connectors.list.details")}</summary>
        <p className="mt-1 break-all">SHA-256 <code className="font-mono">{download.sha256}</code></p>
      </details>
    </div>
  );
}

/** The line that installs, starts and pairs the connector on Linux and macOS, to copy into a terminal. */
function InstallCommand({ command }: { command: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <CommandWithCopy command={command} />
      <p className="app-help-text text-xs">{t("connectors.download.commandNote")}</p>
    </div>
  );
}

function CommandWithCopy({ command }: { command: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <CommandLine command={command} className="max-w-full break-all" />
      <CopyButton value={command}>{t("connectors.download.copyCommand")}</CopyButton>
    </div>
  );
}

/**
 * Before pairing, no connection to the PC exists to ask through (F85), so "Open DMW Connector"
 * tries the app's link, with a new ticket of this account in it (F122): the app shows the
 * server and this account, and once the person connects there, the PC pairs without another
 * approval here. Without a ticket, such as when making one failed, the link still pairs, with
 * the approval in the browser; until the live refusal of every ticket is fixed (29 Oct), the
 * link carries none. Whether an app answered can't be known; a page that keeps the
 * focus for two seconds only adds the other ways, as Zoom, Slack and Teams do. It never claims
 * the app is there. Without a server address to pair with, there is nothing to offer.
 */
export function ConnectThisPc() {
  const loadAddress = useCallback((signal: AbortSignal) => readConnectorServerAddress(signal), []);
  const address = useLoad(loadAddress).state;
  return address.status === "loaded" && address.value !== null ? <ConnectThisPcButton origin={address.value} /> : null;
}

function ConnectThisPcButton({ origin }: { origin: string }) {
  const { t } = useTranslation();
  const [nothingOpened, setNothingOpened] = useState(false);
  const waiting = useRef<number | null>(null);
  useEffect(() => () => {
    if (waiting.current !== null) window.clearTimeout(waiting.current);
  }, []);
  const tryApp = () => {
    setNothingOpened(false);
    let left = false;
    const leave = () => { left = true; };
    window.addEventListener("blur", leave, { once: true });
    // Paused (29 Oct): every ticket was refused on the live server, cause not yet known, so
    // the link pairs with the approval in the browser until it is fixed (F122).
    window.location.assign(connectorPairingLink(origin, null));
    if (waiting.current !== null) window.clearTimeout(waiting.current);
    waiting.current = window.setTimeout(() => {
      window.removeEventListener("blur", leave);
      setNothingOpened(!left && !document.hidden);
    }, APP_LINK_WAIT_MILLISECONDS);
  };
  return (
    <>
      <AppButton type="button" variant="secondary" size="sm" onClick={tryApp}>{t("connectors.download.connect")}</AppButton>
      {nothingOpened ? <p role="status" className="app-help-text text-xs">{t("connectors.download.nothingOpened")}</p> : null}
    </>
  );
}
